<template>
    <div id="cutting-and-pleating-page">
        <div class="top">
            <h2>{{ trans('pages.cutting_pleating.title') }}</h2>
            <ui5-button
                :disabled="exporting"
                @click="exportCsv"
            >
                <spinner v-if="exporting" />
                {{ trans('pages.cutting_pleating.export_csv') }}
            </ui5-button>
        </div>
        <div
            class="loader-spinner"
            v-if="loadingExportable"
        >
            <spinner-text>
                {{ trans('pages.cutting_pleating.loading_products') }}
            </spinner-text>
        </div>
        <template v-else>
            <ui5-list v-if="products">
                <ui5-li
                    v-for="product in products"
                    @click="loadProductPage(product.productId)"
                    :key="'exportable.' + product.productId"
                    :description="getProductDescription(product)"
                >
                    <div>
                        <span>
                            {{ product.productNr }} -
                            {{
                                product.name ||
                                trans(
                                    'pages.cutting_pleating.no_product_reference'
                                )
                            }}
                        </span>
                    </div>
                </ui5-li>
            </ui5-list>

            <h2>{{ trans('pages.cutting_pleating.import_csv') }}</h2>
            <form ref="importForm">
                <input
                    type="file"
                    multiple
                    @change="uploadFiles($event.target.files)"
                    :disabled="importing"
                />
            </form>
            <ul>
                <li
                    v-for="(item, index) in progressList"
                    :key="item.filename + '.' + index"
                >
                    {{
                        trans('pages.cutting_pleating.processing_file', {
                            filename: item.filename,
                        })
                    }}
                    <template v-if="item.status !== null">
                        {{ item.status ? ' OK' : ' error' }}
                    </template>
                    <spinner v-else />
                </li>
            </ul>

            <ui5-list v-if="resultList">
                <ui5-li
                    v-for="(item, index) in uniqueResults"
                    @click="loadProductPage(item.id)"
                    :key="'result.' + index + '.' + item.id"
                    :description="getResultDescription(item)"
                    :additional-text="getAdditionalText(item)"
                    :additional-text-state="getAdditionalTextState(item)"
                >
                    <div>
                        <template v-if="item.product">
                            {{ item.product.productNr }} -
                            {{
                                item.product.name ||
                                trans(
                                    'pages.cutting_pleating.no_product_reference'
                                )
                            }}
                        </template>
                        <template v-else>
                            {{
                                trans('pages.cutting_pleating.unknown_error', {
                                    error: item,
                                })
                            }}
                        </template>
                    </div>
                </ui5-li>
            </ui5-list>
        </template>
    </div>
</template>
<script>
import SpinnerText from '../components/loader/SpinnerText';
import cuttingPleatingApi from '../api/cuttingPleating';
import Spinner from '../components/loader/Spinner';
import { saveAs } from 'file-saver';
import { showErrorMessage } from '../dialogs';
import _ from 'lodash';
import localization from '../mixins/localization';

const SUCCESS = 1;
const FAIL = 0;
const SKIP = -1;

export default {
    components: { Spinner, SpinnerText },
    mixins: [localization],
    data() {
        return {
            exporting: false,
            loadingExportable: false,
            products: null,
            importing: false,
            progressList: [],
            resultList: [],
        };
    },
    computed: {
        uniqueResults() {
            return _.uniq(this.resultList, function (x) {
                return x.productId;
            });
        },
    },
    methods: {
        getResultDescription(item) {
            return !item.product
                ? ''
                : item.product.orderId +
                      ' - ' +
                      (item.product.reference ||
                          this.trans(
                              'pages.cutting_pleating.no_order_reference'
                          )) +
                      ' - ' +
                      item.product.productstatus;
        },
        getProductDescription(product) {
            return (
                product.orderId +
                ' - ' +
                (product.reference ||
                    this.trans('pages.cutting_pleating.no_order_reference'))
            );
        },
        getAdditionalTextState(item) {
            switch (item.success) {
                case SUCCESS:
                    return 'Success';
                case FAIL:
                    return 'Error';
                case SKIP:
                    return 'Warning';
            }
            return 'None';
        },
        getAdditionalText(item) {
            switch (item.success) {
                case SUCCESS:
                    return this.trans(
                        'pages.cutting_pleating.result_states.ok'
                    );
                case FAIL:
                    switch (item.error) {
                        case 'wrong.producttype':
                            return this.trans(
                                'pages.cutting_pleating.error_types.wrong_producttype'
                            );
                        case 'invalid.length':
                            return this.trans(
                                'pages.cutting_pleating.error_types.invalid_length'
                            );
                        case 'length.set.fail':
                            return this.trans(
                                'pages.cutting_pleating.error_types.length_set_fail'
                            );
                        case 'status.set.fail':
                            return this.trans(
                                'pages.cutting_pleating.error_types.status_set_fail'
                            );
                    }
                    return this.trans(
                        'pages.cutting_pleating.result_states.failed'
                    );
                case SKIP:
                    return this.trans(
                        'pages.cutting_pleating.result_states.skipped'
                    );
            }
            return '';
        },
        loadProductPage(productId) {
            this.$router.push('/productpage/' + productId);
        },
        uploadFiles(files) {
            const self = this;
            const formData = new FormData();
            formData.append(
                'employeeId',
                self.$store.getters['currentUser/getUserId']
            );

            const progressObjects = [];
            Array.from(files).forEach((file) => {
                formData.append('csv[]', file);
                const progressObject = { filename: file.name, status: null };
                self.progressList.push(progressObject);
                progressObjects.push(progressObject);
            });

            self.importing = true;
            cuttingPleatingApi
                .importCsv(formData)
                .then((response) => {
                    progressObjects.forEach((x) => {
                        x.status = true;
                    });
                    response.data.data.forEach((item) => {
                        this.resultList.push(item);
                    });
                })
                .catch(() => {
                    progressObjects.forEach((x) => {
                        x.status = false;
                    });
                })
                .finally(() => {
                    self.importing = false;
                    self.$refs.importForm.reset();
                });
        },
        exportCsv() {
            this.exporting = true;
            cuttingPleatingApi
                .exportCuttingPleatingCsv()
                .then((response) => {
                    const responseData = response.data.data;

                    var blob = new Blob([responseData.csv], {
                        type: 'text/csv;charset=utf-8;',
                    });

                    saveAs(blob, 'AutogenPrgV2.csv');

                    responseData.autoCsvImport.forEach((item) => {
                        this.resultList.push(item);
                    });
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.cutting_pleating.error_export_csv')
                    );
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
        loadExportableProducts() {
            this.loadingExportable = true;
            cuttingPleatingApi
                .fetchExportableProducts()
                .then((response) => {
                    this.products = response.data.data;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.cutting_pleating.error_fetch_products'
                        )
                    );
                })
                .finally(() => {
                    this.loadingExportable = false;
                });
        },
    },
    mounted() {
        this.loadExportableProducts();
    },
};
</script>
